// Shared sync operations contain no story catalogue or browser dependencies.
import type {BookmarkDetails} from './bookmarks.ts';
export type Progress = {index:number;visited:number[];updatedAt:number;contentRevision?:number};
export type Journal = {version:1;catalogueRevision:2;saved:string[];bookmarks?:Record<string,BookmarkDetails>;reactions:Record<string,'like'|'dislike'>;continueDismissed?:Record<string,number>;progress:Record<string,Progress>;completed:Record<string,{date:string;timestamp:number}>;activeDays:string[];textSize:number;theme:'paper'|'night'};
export type StoryChange = {id:string;saved?:boolean;bookmark?:BookmarkDetails|null;reaction?:'like'|'dislike'|null;continueDismissed?:number;progress?:Progress;completed?:{date:string;timestamp:number}};
export type JournalPatch = {stories:StoryChange[];settings?:{textSize:number;theme:'paper'|'night'}};
export const blankJournal = ():Journal=>({version:1,catalogueRevision:2,saved:[],reactions:{},progress:{},completed:{},activeDays:[],textSize:1,theme:'night'});
const equal=(a:unknown,b:unknown)=>JSON.stringify(a)===JSON.stringify(b);
export function journalDiff(base:Journal,local:Journal,limit=10):JournalPatch {
  const ids=new Set([...base.saved,...local.saved,...Object.keys(base.bookmarks??{}),...Object.keys(local.bookmarks??{}),...Object.keys(base.reactions),...Object.keys(local.reactions),...Object.keys(local.progress),...Object.keys(local.completed),...Object.keys(local.continueDismissed??{})]);
  const stories:StoryChange[]=[];
  for(const id of ids){
    const change:StoryChange={id};
    if(base.saved.includes(id)!==local.saved.includes(id))change.saved=local.saved.includes(id);
    if(!equal(base.bookmarks?.[id],local.bookmarks?.[id]))change.bookmark=local.bookmarks?.[id]??null;
    if(base.reactions[id]!==local.reactions[id])change.reaction=local.reactions[id]??null;
    if((local.continueDismissed?.[id]??0)>(base.continueDismissed?.[id]??0))change.continueDismissed=local.continueDismissed![id];
    if(local.progress[id]&&!equal(base.progress[id],local.progress[id]))change.progress=local.progress[id];
    if(local.completed[id]&&!equal(base.completed[id],local.completed[id]))change.completed=local.completed[id];
    if(Object.keys(change).length>1)stories.push(change);
    if(stories.length>=limit)break;
  }
  return {stories,...(base.textSize!==local.textSize||base.theme!==local.theme?{settings:{textSize:local.textSize,theme:local.theme}}:{})};
}
export const hasChanges=(patch:JournalPatch)=>patch.stories.length>0||Boolean(patch.settings);
export function applyJournalPatch(base:Journal,patch:JournalPatch):Journal {
  const next:Journal={...base,saved:[...base.saved],bookmarks:{...base.bookmarks},progress:{...base.progress},reactions:{...base.reactions},completed:{...base.completed},continueDismissed:{...base.continueDismissed}};
  for(const change of patch.stories){
    const {id}=change;
    if(change.saved!==undefined)next.saved=change.saved?[...new Set([...next.saved,id])]:next.saved.filter(item=>item!==id);
    if(change.bookmark===null||change.saved===false)delete next.bookmarks![id];
    else if(change.bookmark){next.bookmarks![id]={...change.bookmark};next.saved=[...new Set([...next.saved,id])];}
    if(change.reaction===null)delete next.reactions[id];
    else if(change.reaction!==undefined)next.reactions[id]=change.reaction;
    if(change.continueDismissed!==undefined)next.continueDismissed![id]=Math.max(next.continueDismissed![id]??0,change.continueDismissed);
    if(change.progress){
      const old=next.progress[id];
      const incomingRevision=change.progress.contentRevision,oldRevision=old?.contentRevision;
      if(incomingRevision!==undefined&&oldRevision!==undefined&&incomingRevision!==oldRevision){
        next.progress[id]=incomingRevision>oldRevision?{...change.progress,visited:[...change.progress.visited]}:old;
      }else{
      const latest=!old||change.progress.updatedAt>=old.updatedAt?change.progress:old;
      next.progress[id]={...latest,visited:[...new Set([...(old?.visited??[]),...change.progress.visited])].sort((a,b)=>a-b)};
      }
    }
    if(change.completed&&(!next.completed[id]||change.completed.timestamp<next.completed[id].timestamp))next.completed[id]=change.completed;
  }
  if(patch.settings)Object.assign(next,patch.settings);
  next.saved.sort();next.activeDays=[...new Set(Object.values(next.completed).map(entry=>entry.date))].sort();
  return next;
}
export function mergeGuestJournal(cloud:Journal,guest:Journal):Journal {
  const patch=journalDiff(blankJournal(),guest,1000);
  // Import only positive guest actions; never erase existing cloud data/settings.
  delete patch.settings;
  for(const change of patch.stories){
    if(!change.saved)delete change.saved;
    if(cloud.reactions[change.id])delete change.reaction;
    if(cloud.bookmarks?.[change.id])delete change.bookmark;
  }
  return applyJournalPatch(cloud,patch);
}
